import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { SuscripcionDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { daysFromToday, formatSpokenDate } from '../../../utils/dates'
import {
    errorMessage,
    money,
    PERIODICIDAD_LABELS,
    planFeature,
    PLANS_PATH,
    PRICE_SUFFIX,
    SUBSCRIPTION_BACK_PATH,
    SUBSCRIPTION_PATH,
} from '../common/subscription-common'

const LOAD_ERROR = 'No se pudo cargar tu suscripción.'

const remainingLabel = (days: number | null) => {
    if (days == null) return null
    if (days > 1) return `Faltan ${days} días`
    if (days === 1) return 'Falta 1 día'
    if (days === 0) return 'Vence hoy'
    return `Venció hace ${Math.abs(days)} ${Math.abs(days) === 1 ? 'día' : 'días'}`
}

export const useSubscriptionViewModel = () => {
    const location = useLocation()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || SUBSCRIPTION_BACK_PATH

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [suscripcion, setSuscripcion] = useState<SuscripcionDTO | null>(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response: WrapperResponse<SuscripcionDTO | null> | null = await (await APIInvoke.invokeGET('/suscripciones/activa')).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                setSuscripcion(response.body)
            } catch (loadError) {
                console.error('Error al cargar la suscripción activa', loadError)
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [])

    const plan = suscripcion?.plan ?? null
    const periodicidad = plan?.periodicidad ?? null
    const isTrial = periodicidad === 'prueba'
    const days = daysFromToday(suscripcion?.vigenciaA)
    const expired = days != null && days < 0

    return {
        loading,
        error,
        hasSubscription: Boolean(plan),
        planName: plan?.nombre || 'Sin plan',
        periodLabel: periodicidad ? PERIODICIDAD_LABELS[periodicidad] : null,
        price: plan && !isTrial ? money(plan.precio) : null,
        priceSuffix: periodicidad ? PRICE_SUFFIX[periodicidad] || '' : '',
        isTrial,
        expired,
        renewalTitle: isTrial ? 'Tu prueba termina el' : expired ? 'Tu suscripción venció el' : 'Tu suscripción se renovará el',
        renewalDate: formatSpokenDate(suscripcion?.vigenciaA) || '—',
        remaining: remainingLabel(days),
        startDate: formatSpokenDate(suscripcion?.vigenciaDe),
        features: (plan?.items || []).map(planFeature),
        portalUrl: !isTrial ? suscripcion?.portalUrl || null : null,
        canHire: !plan || isTrial,
        backTo: backUrl,
        plansLocation: { pathname: PLANS_PATH, state: { backUrl: SUBSCRIPTION_PATH } },
    }
}
